// Eskom PARCC — human labels for the audit action codes (PURE).
//
// Shared by the admin activity feed (AuditPanel), the per-transaction timeline and
// the global Audit Trail screen, so an action reads the same everywhere. `actionLabel`
// gives the verb ("Updated transaction"); `surfaceLabel` gives the WHERE Lindiwe
// asked for ("Manage edit" / "Bulk update" / "Import" / "CES capture" / "Admin").
// (admin-api/digest.js keeps its own map — server-side, a different purpose.)

/** Human labels for every audit action code written across the app. */
export const ACTION_LABELS: Record<string, string> = {
  // Data snapshots (scheduled + restore)
  snapshot_taken: 'Took data snapshot',
  snapshot_failed: 'Data snapshot failed',
  snapshot_restored: 'Restored data snapshot',
  snapshot_restore_failed: 'Snapshot restore failed',
  // Access / account
  grant_access: 'Granted access',
  update_role: 'Changed role',
  revoke_access: 'Revoked access',
  create_credentials: 'Created credentials',
  set_password: 'Set password',
  update_profile: 'Updated profile',
  update_own_digest_cadence: 'Changed digest cadence',
  revoke_own_sessions: 'Revoked own sessions',
  // Transactions & review
  insert_transaction: 'Created transaction',
  update_transaction: 'Updated transaction',
  bulk_update_transaction: 'Bulk-updated transaction',
  bulk_update: 'Bulk update',
  soft_delete_transaction: 'Deleted transaction',
  restore_transaction: 'Restored transaction',
  restore_transaction_fresh: 'Restored transaction (fresh)',
  rename_reference: 'Renamed reference',
  save_working_paper: 'Saved working paper',
  replace_findings: 'Edited findings', // historical (pre-RTM-Workstream-I audit rows)
  reconcile_findings: 'Edited findings',
  update_finding: 'Edited finding',
  backfill_findings: 'Backfilled findings',
  replace_ces: 'Edited CES rows',
  ces_capture: 'Captured CES',
  ces_upload: 'Uploaded CES workbook',
  allocate: 'Allocated work',
  // QA notes
  qa_note_added: 'Added QA note',
  qa_note_resolved: 'Resolved QA note',
  qa_note_reopened: 'Reopened QA note',
  // Bulk / report registers
  import_population: 'Imported population',
  import_rfi: 'Imported RFI register',
  upsert_rtm: 'Updated RTM sign-off',
  purge_population: 'Removed population',
  restore_population: 'Restored population',
  email_report: 'Emailed report',
  save_report_narrative: 'Saved report narrative',
  reset_report_narrative: 'Reset report narrative',
  save_rtm_status: 'Saved reports-to-management status',
  backfill_year: 'Backfilled financial year',
  // AI engine
  ai_review_start: 'Started AI review',
  ai_review_done: 'Completed AI review',
  ai_review_reused: 'Reused AI review',
  ai_evidence_open: 'Opened AI evidence',
  ai_ocr_start: 'Started OCR',
  ai_ocr_harvest: 'Harvested OCR',
};

/** The verb for an action code — falls back to the raw code if unmapped. */
export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

/** Which surface an action came from — the *where*. */
const SURFACE_LABELS: Record<string, string> = {
  insert_transaction: 'Manage edit',
  update_transaction: 'Manage edit',
  soft_delete_transaction: 'Manage edit',
  restore_transaction: 'Manage edit',
  restore_transaction_fresh: 'Manage edit',
  rename_reference: 'Manage edit',
  save_working_paper: 'Manage edit',
  replace_findings: 'Manage edit', // historical (pre-RTM-Workstream-I audit rows)
  reconcile_findings: 'Manage edit',
  update_finding: 'Manage edit',
  replace_ces: 'Manage edit',
  bulk_update_transaction: 'Bulk update',
  bulk_update: 'Bulk update',
  import_population: 'Import',
  import_rfi: 'Import',
  purge_population: 'Import',
  restore_population: 'Import',
  backfill_findings: 'Import',
  ces_capture: 'CES capture',
  ces_upload: 'CES capture',
  save_report_narrative: 'Reports',
  reset_report_narrative: 'Reports',
  save_rtm_status: 'Reports',
};

/**
 * The surface an action came from — the *where* Lindiwe asked for. Everything not
 * a transaction/import/CES write (access grants, profile, snapshots, AI, RTM, QA,
 * allocations) is an administrative action → "Admin".
 */
export function surfaceLabel(action: string): string {
  return SURFACE_LABELS[action] ?? 'Admin';
}
